import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Linkedin, Twitter, Youtube } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    'Produit': [
      { name: 'Modules de Formation', href: '#piliers' },
      { name: 'Coach IA Vocal', href: '#coach-ia' },
      { name: 'Espace Coachs', href: '/marketplace' },
      { name: 'Coaching Humain', href: '#coaching-humain' },
      { name: 'Tarifs', href: '#plans' },
      { name: 'Témoignages', href: '#temoignages' }
    ],
    'Entreprise': [
      { name: 'À Propos', href: '/a-propos' },
      { name: 'Information au public', href: '/information-publique' },
      { name: 'Contact', href: '/contact' }
    ],
    'Légal': [
      { name: 'Mentions Légales', href: '/mentions-legales' },
      { name: 'CGV', href: '/cgv' }
    ]
  };

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Youtube, href: '#', label: 'YouTube' },
    { icon: Mail, href: 'mailto:contact@Coach-dia.com', label: 'Email' }
  ];

  return (
    <footer 
      className="text-white pt-16 pb-8 relative overflow-hidden"
      style={{
        background: 'linear-gradient(90deg, #FDE68A 0%, #C4B5FD 50%, #F9A8D4 100%)'
      }}
    >
      {/* Decorative overlay */}
      <div className="absolute inset-0 opacity-30" style={{
        background: 'radial-gradient(circle at 50% 50%, rgba(255, 255, 255, 0.3), transparent)'
      }} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Brand Column */}
          <div className="md:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-6"
            >
              <h3
                className="text-2xl font-black mb-2"
                style={{ 
                  fontFamily: "'Manrope', sans-serif",
                  color: '#1C1147'
                }}
              >
                <span>Coach</span>
                <br />
                <span style={{ color: '#F59E0B' }}>DIA</span>
              </h3>
              <p className="text-sm" style={{ color: '#3D2B73', fontWeight: 500 }}>
                La renaissance du leadership — là où l'humain et l'IA se subliment.
              </p>
              <p className="text-xs mt-2" style={{ color: '#6B5B95' }}>
                Coach DIA n'est pas un chatbot. C'est une formation managériale augmentée, évaluée sur la pratique.
              </p>
            </motion.div>

            {/* Social Links */}
            <div className="flex space-x-4">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-md"
                  style={{
                    background: 'rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(10px)',
                    color: '#1C1147'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#FFD84D';
                    e.currentTarget.style.color = '#1C1147';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.color = '#1C1147';
                  }}
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([category, links], categoryIndex) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: categoryIndex * 0.1 }}
            >
              <h4 className="font-bold mb-4" style={{ color: '#1C1147' }}>
                {category}
              </h4>
              <ul className="space-y-2">
                {links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="transition-colors text-sm hover:underline"
                      style={{ color: '#3D2B73', fontWeight: 500 }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#F59E0B';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#3D2B73';
                      }}
                    >
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Divider */}
        <div className="my-8" style={{ borderTop: '1px solid rgba(255, 255, 255, 0.3)' }} />

        {/* Quote Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <p
            className="text-xl mb-2 italic"
            style={{ 
              fontFamily: "'Manrope', sans-serif",
              color: '#1C1147',
              fontWeight: 600
            }}
          >
            Former &#233;tait insuffisant. Transformer devient la norme.
          </p>
        </motion.div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-sm" style={{ color: '#3D2B73', fontWeight: 500 }}>
            &copy; {currentYear} Coach DIA. Tous droits r&#233;serv&#233;s.
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#F4D03F]" />
            <span className="text-sm" style={{ color: '#1C1147', fontWeight: 500 }}>
              L&#224; o&#249; le leadership devient mesurable
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
